import React, { useEffect, useState, useRef } from "react";
import { POPULATION_DATA } from "../../shared/population-colors";
import { useIsMobile } from "../../hooks/useIsMobile";
import CollapseButton from "../SearchForm/components/CollapseButton";

type LeyendaProps = {
  minZoom?: number;
  maxZoom?: number;
  defaultZoom?: number;
  currentZoom?: number;
  onZoomChange?: (zoom: number) => void;
  onLegendToggle?: (isOpen: boolean) => void;
  onLegendWidthChange?: (widthPx: number) => void;
};

const ACCENT = "#ce0047";
const PANEL_W_DESKTOP = 320;
const PANEL_W_MOBILE = 250;

const Leyenda: React.FC<LeyendaProps> = ({
  minZoom = 2.0,
  maxZoom = 7.5,
  defaultZoom = 4.6,
  currentZoom,
  onZoomChange,
  onLegendToggle,
  onLegendWidthChange,
}) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(!isMobile);
  const [zoom, setZoom] = useState(defaultZoom);
  const panelRef = useRef<HTMLDivElement>(null);

  const panelWidthPx = isMobile ? PANEL_W_MOBILE : PANEL_W_DESKTOP;

  // sincroniza zoom con el mapa externo
  useEffect(() => {
    if (typeof currentZoom === "number") {
      setZoom(Number(currentZoom.toFixed(2)));
    }
  }, [currentZoom]);

  // alternar según dispositivo
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  // notificar cambio de estado (abierto/cerrado)
  useEffect(() => {
    onLegendToggle?.(open);
  }, [open, onLegendToggle]);

  // notificar ancho actual al padre
  useEffect(() => {
    const notifyWidth = () => {
      if (!panelRef.current) return;
      const width = open
        ? Math.round(panelRef.current.getBoundingClientRect().width)
        : 0;
      onLegendWidthChange?.(width);
    };

    const ro = new ResizeObserver(() => notifyWidth());
    if (panelRef.current) ro.observe(panelRef.current);
    notifyWidth();

    return () => ro.disconnect();
  }, [open, isMobile, onLegendWidthChange]);

  const populationData = Object.entries(POPULATION_DATA).map(([key, value]) => ({
    key,
    ...value,
  }));

  const handleZoom = (v: number) => {
    setZoom(v);
    onZoomChange?.(v);
  };

  return (
    <div
      ref={panelRef}
      className="relative pointer-events-none select-none"
      style={{ width: panelWidthPx }}
    >
      {/* 🔹 Botón colapsar/expandir (mismo estilo que SearchForm) */}
      <CollapseButton
        isOpen={open}
        panelWidthPx={panelWidthPx}
        onToggle={() => setOpen((v) => !v)}
        positioning="absolute"
        isLegend={true}
      />

      {/* Panel principal */}
      <div
        className={[
          "will-change-transform transition-all duration-300",
          open
            ? "translate-x-0 opacity-100 pointer-events-auto"
            : "translate-x-[110%] opacity-0 pointer-events-none",
        ].join(" ")}
        style={{ width: panelWidthPx }}
      >
        <div className="rounded-xl shadow-2xl bg-white text-gray-900 border border-gray-300 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-[15px] font-semibold">Centros de cuidado</h2>
          </div>

          <div className="px-4 py-3">
            {/* Colores de poblaciones */}
            <ul className="space-y-2">
              {populationData.map((pop) => (
                <li key={pop.key} className="flex items-center gap-3">
                  <div
                    className="h-[18px] w-[44px] rounded-md border border-gray-300"
                    style={{ background: pop.color }}
                  />
                  <p className="text-[13px]">{pop.label}</p>
                </li>
              ))}
              <li className="flex items-center gap-3">
                <div className="h-[18px] w-[44px] rounded-md border border-gray-300 bg-gray-600" />
                <p className="text-[13px]">Dos o más poblaciones</p>
              </li>
            </ul>

            <div className="mt-3 h-px w-full bg-gray-200" />

            {/* Slider de zoom */}
            <div className="mt-3">
              <p className="text-[12px] text-gray-600 mb-2">Nivel de zoom</p>
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-gray-500 w-8 text-right">
                  {minZoom.toFixed(1)}
                </span>

                <input
                  type="range"
                  min={minZoom}
                  max={maxZoom}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => handleZoom(Number(e.target.value))}
                  className="flex-1 h-2 rounded-full bg-gray-200 accent-[#ce0047] appearance-none"
                  style={{ WebkitAppearance: "none" as any }}
                />

                <span className="text-[12px] text-gray-500 w-8">
                  {maxZoom.toFixed(1)}
                </span>
                <span
                  className="text-[12px] font-medium text-accent"
                >
                  {zoom.toFixed(1)}x
                </span>
              </div>
            </div>

            <p className="mt-3 text-[12px] text-gray-500 leading-snug">
              * El mapa muestra solo la oferta pública reportada; la ausencia de
              puntos no garantiza que un municipio carezca de centros de cuidado.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leyenda;
